import moment from 'moment';

const DATE_FORMAT = 'DD/MM/YYYY';

/**
 * Formats a single conflicting add-on period the same way the rest of the UI does.
 * Returns null when the period cannot be rendered, so the caller can degrade gracefully.
 */
const formatConflictPeriod = (period) => {
	if (!period || typeof period !== 'object') {
		return null;
	}

	if (period.startOnActivation) {
		return 'Starts on activation';
	}

	// These are calendar dates stored at UTC midnight (see client/helpers/addonPeriod.js),
	// so they are parsed in UTC here too, regardless of the browser's local timezone.
	const start = period.startDate && moment.utc(period.startDate).isValid() ? moment.utc(period.startDate).format(DATE_FORMAT) : null;
	const end = period.endDate && moment.utc(period.endDate).isValid() ? moment.utc(period.endDate).format(DATE_FORMAT) : null;

	if (!start || !end) {
		return null;
	}

	return `${start} – ${end}`;
};

/**
 * Builds the legible overlap message from the 409 body's structured `conflicts` array.
 * Returns null on anything malformed/partial so the caller can fall back to `detail.message`.
 */
const buildConflictsMessage = (conflicts) => {
	if (!Array.isArray(conflicts) || conflicts.length === 0) {
		return null;
	}

	const blocks = [];

	for (const conflict of conflicts) {
		if (!conflict || !Array.isArray(conflict.periods) || conflict.periods.length !== 2) {
			return null;
		}

		const addonName = typeof conflict.addonName === 'string' && conflict.addonName.trim() ? conflict.addonName.trim() : 'This license';
		const [firstPeriod, secondPeriod] = conflict.periods;
		const firstText = formatConflictPeriod(firstPeriod);
		const secondText = formatConflictPeriod(secondPeriod);

		if (!firstText || !secondText) {
			return null;
		}

		blocks.push(`${addonName}: ${firstText}\n${addonName}: ${secondText}`);
	}

	if (blocks.length === 0) {
		return null;
	}

	return `Overlapping license periods are not allowed:\n\n${blocks.join('\n\n')}\n\nA new period must start at least one day after the previous one ends.`;
};

/**
 * Extracts messages out of a FastAPI-style validation error array, e.g.
 * [{ message }] / [{ msg }] / [{ loc, msg, type }]. Returns null when nothing usable is found.
 */
const extractValidationMessages = (detail) => {
	if (!Array.isArray(detail) || detail.length === 0) {
		return null;
	}

	const messages = detail
		.map((entry) => {
			if (typeof entry === 'string') {
				return entry.trim() ? entry : null;
			}

			if (!entry || typeof entry !== 'object') {
				return null;
			}

			if (typeof entry.message === 'string' && entry.message) {
				return entry.message;
			}

			if (typeof entry.msg === 'string' && entry.msg) {
				return entry.msg;
			}

			return null;
		})
		.filter(Boolean);

	if (messages.length === 0) {
		return null;
	}

	return messages.join('\n');
};

/**
 * A 422 of the v1 envelope says only "Request validation failed." in `message`; the sentences written for the
 * user sit in `details.fieldErrors` as `{field, message}`. They are listed under the message, one per line.
 */
const appendFieldErrors = (message, details) => {
	const fieldErrors = details && typeof details === 'object' ? details.fieldErrors : null;

	if (!Array.isArray(fieldErrors) || fieldErrors.length === 0) {
		return message;
	}

	const lines = fieldErrors
		.map((fieldError) => {
			if (!fieldError || typeof fieldError !== 'object' || typeof fieldError.message !== 'string' || !fieldError.message.trim()) {
				return null;
			}

			return typeof fieldError.field === 'string' && fieldError.field.trim() ? `${fieldError.field}: ${fieldError.message}` : fieldError.message;
		})
		.filter(Boolean);

	if (lines.length === 0) {
		return message;
	}

	return `${message}\n${lines.join('\n')}`;
};

/**
 * Turns any error our services can produce into a non-empty, human-readable string.
 * Never throws and never returns a non-string, so it is always safe to hand to
 * pushDangerNotification / <Toast.Body>.
 */
export const getApiErrorMessage = (error, fallback = 'An error occurred') => {
	if (typeof error === 'string') {
		return error.trim() ? error : fallback;
	}

	const data = error?.response?.data;
	const detail = data?.detail;

	if (detail && typeof detail === 'object' && !Array.isArray(detail) && Array.isArray(detail.conflicts)) {
		const conflictsMessage = buildConflictsMessage(detail.conflicts);

		if (conflictsMessage) {
			return conflictsMessage;
		}
	}

	/*
	 * The rest-api routes added from SYN-3378 on answer `{"error": {"code", "message", "details"}}`.
	 * `message` is the sentence written for the user, so it wins over the generic branches below.
	 */
	if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string' && data.error.message.trim()) {
		return appendFieldErrors(data.error.message, data.error.details);
	}

	if (typeof data?.message === 'string' && data.message) {
		return data.message;
	}

	if (typeof detail === 'string' && detail) {
		return detail;
	}

	if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message) {
		return detail.message;
	}

	const validationMessage = extractValidationMessages(detail);

	if (validationMessage) {
		return validationMessage;
	}

	// A non-JSON error body (proxy/gateway text) - only reachable once none of the richer
	// structured shapes above matched, since a string `data` has no .message/.detail to match.
	// A serialised JSON object/array here means something upstream failed to parse the body
	// (or wrapped it in quotes) - surfacing it verbatim would leak raw JSON, so skip it and
	// fall through to the remaining branches instead.
	const trimmedData = typeof data === 'string' ? data.trim() : '';

	if (trimmedData && trimmedData[ 0 ] !== '{' && trimmedData[ 0 ] !== '[') {
		return data;
	}

	if (typeof error?.message === 'string' && error.message) {
		return error.message;
	}

	return fallback;
};

export default { getApiErrorMessage };
